import { ICON_COLOR_KEYS } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';

import { XStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';

import { AgentAvatar } from './agents-avatar';
import { BIRD_INK } from './agents-bird.look';
import { useAgentCustomize } from './agents.hooks';
import { type AgentEntry } from './agents.store';

type AgentCustomizeDialogProps = {
  agent: AgentEntry;
  onClose: () => void;
};

export const AgentCustomizeDialog = observer(
  ({ agent, onClose }: AgentCustomizeDialogProps) => {
    const customize = useAgentCustomize(agent, onClose);
    const theme = uiStore.theme === 'dark' ? 'dark' : 'light';

    return (
      <Dialog.Root
        open
        onOpenChange={(open) => {
          if (!open && !customize.isSaving) onClose();
        }}
      >
        <Dialog.Content className="max-w-[22rem] justify-items-center gap-5">
          <Dialog.Title className="justify-self-start">Customize</Dialog.Title>
          <Dialog.Description className="sr-only">
            Choose a color and a name for {agent.name}.
          </Dialog.Description>
          <AgentAvatar bird={customize.look} className="size-32" />
          <input
            aria-label="Name"
            value={customize.name}
            maxLength={80}
            className="w-full truncate bg-transparent text-center text-lg font-medium tracking-tight text-foreground-subtle outline-none placeholder:text-foreground-muted"
            placeholder="Name"
            onChange={(event) => customize.setName(event.target.value)}
          />
          <XStack
            role="group"
            aria-label="Color"
            className="flex-wrap justify-center gap-2.5"
          >
            {ICON_COLOR_KEYS.map((plumage) => (
              <button
                key={plumage}
                type="button"
                aria-label={plumage}
                aria-pressed={plumage === customize.look.plumage}
                className={cn(
                  'size-7 rounded-full ring-offset-2 ring-offset-background-subtle focus-ring',
                  plumage === customize.look.plumage && 'ring-2 ring-foreground'
                )}
                style={{ backgroundColor: BIRD_INK[plumage][theme] }}
                onClick={() => customize.pickPlumage(plumage)}
              />
            ))}
          </XStack>
          <Button
            className="w-full"
            disabled={!customize.canSave}
            isLoading={customize.isSaving}
            loadingText="Saving"
            onClick={() => void customize.save()}
          >
            Save
          </Button>
        </Dialog.Content>
      </Dialog.Root>
    );
  }
);
